<template>
    <feature-flag-app>
        <loader v-show="isLoading" />
        <v-layout>
            <v-container data-app v-if="!isLoading && showEmailVerify">
                <NsCard 
                    context="warning" 
                    data-test-id="notVerifiedNotPendingCard"
                    v-show="!isEmailVerified && !isPendingStatus"
                >
                    <template v-slot:header>
                        {{sharedStrings.email_verification.email_verify_card_verify_header}}
                    </template>
                    <template v-slot:content>
                        <p class="medium-p">
                            {{ 
                                isUsernameStatus
                                    ? `${sharedStrings.email_verification.email_verify_card_verify_content}`
                                    : `${sharedStrings.email_verification.email_username_update_content}`
                            }}
                            
                        </p>
                        <div class="medium-p current-email">
                            <p>{{sharedStrings.email_verification.email_verify_card_verify_current_username}}</p>
                            <p class="subhead-l">{{ getUsername }}</p>
                        </div>
                        <button 
                            class="primary fluid"
                            id="verifyEmailBtn"
                            style="margin-bottom: 10px;"
                            @click="handleVerifyEmail()"
                            v-if="isUsernameStatus"
                        >
                            {{sharedStrings.email_verification.email_verify_card_verify_button}}
                        </button>
                        <button 
                            :class="`${isUsernameStatus ? 'primary-outline' : 'primary'} fluid`"
                            @click.stop="emailDialog = true"
                        >
                            {{sharedStrings.email_verification.email_verify_card_verify_change_button}}
                        </button>
                    </template>
                </NsCard>
                <NsCard 
                    context="success"
                    data-test-id="emailVerifiedCard"
                    color="#252525" 
                    v-if="isEmailVerified" 
                    @onActionClick="handleCloseAction" 
                    has-action
                >
                    <template v-slot:header>
                        {{sharedStrings.email_verification.email_verify_card_success_header}}
                    </template>
                    <template v-slot:content>
                        {{sharedStrings.email_verification.email_verify_card_success_content}}
                    </template>
                </NsCard>
                <NsCard 
                    context="warning"
                    v-if="!isEmailVerified && isPendingStatus"
                    data-test-id="notVerifiedIsPendingCard"
                >
                    <template v-slot:header>
                        {{sharedStrings.email_verification.email_verify_card_info_header}}
                    </template>
                    <template v-slot:content>
                        <p class="small-p">
                            {{
                                !isModified ? 
                                    `${getEmailSentMsg}`
                                    : `${getEmailModifiedMsg}`
                            }}
                        </p>
                        <button
                            class='primary fluid resend-p'
                            @click="handleVerifyEmail()"
                        >
                            {{sharedStrings.email_verification.email_verify_card_info_resend}}
                        </button>
                        <p v-if="isResendVerifyEmail">
                            {{sharedStrings.email_verification.email_verify_card_info_resend_success}}
                        </p>
                    </template>
                </NsCard>
                <NsCard 
                    context="danger"
                    v-if="isEmailSentHavingIssue"
                    data-test-id="emailVerifyErrorCard"
                >
                    <template v-slot:header>
                        {{sharedStrings.email_verification.email_verify_card_error_header}}
                    </template>
                    <template v-slot:content>
                        <p class="small-p">
                            {{errorMsg}}
                        </p>
                    </template>
                </NsCard>
                <edit-username-email-dialog v-if="emailDialog" @closeDialog="handleCloseDialog" />
            </v-container>
        </v-layout>
    </feature-flag-app>
</template>

<script>
import { NsCard } from '@nuskin/design-components'
import { mapGetters, mapActions } from 'vuex'
import profileUtils from '../repositories/profileUtils';
import Loader from './Loader';
import EditUsernameEmailDialog from './forms/EditUsernameEmailDialog';
import { UsesFeatureFlags } from '@nuskin/ns-feature-flags-vue';

export default {
    components: {
        NsCard,
        Loader,
        EditUsernameEmailDialog
    },
    mixins: [UsesFeatureFlags],
    data() {
        return {
            translations: {},
            emailDialog: false,
            isLoading: false
        }
    },
    computed: {
        ...mapGetters({
            mobileView: 'mobileView',
            txc: 'translationsCategories',
            sharedStrings: 'sharedStrings',
            account: 'account',
            isEmailVerified: 'isEmailVerified',
            isResendVerifyEmail: 'isResendVerifyEmail',
            isEmailSentHavingIssue: 'isEmailSentHavingIssue',
            isModified: 'isModified',
            isUsernameStatus: 'isUsernameStatus',
            getUsername: 'getUsername',
            isPendingStatus: 'isPendingStatus',
            pendingUsername: 'pendingUsername',
            errorMsg: 'errorMsg'
        }),
        getEmailSentMsg() {
            return this.sharedStrings.email_verification.email_verify_card_info_sent.replace(
                'XXX',
                this.getUsername
            );
        },
        getEmailModifiedMsg() {
            return this.sharedStrings.email_verification.email_verify_card_info_modified.replace(
                'XXX',
                this.pendingUsername
            );
        },
        showEmailVerify() {
            return Boolean(this.featureEnabled('cx13-15592-enable-email-verify-feature'))
        }
    },
    methods: {
        ...mapActions([
            'setEmailVerified',
            'setPendingStatus',
            'setEmailSentHavingIssue',
            'setResendVerifyEmail',
            'setEmailCardShowing',
            'setAccount',
            'updateEmail',
            'setErrorMsg'
        ]),
        handleCloseAction () {
            localStorage.setItem('successNotificationConfirmed', true)
            this.setEmailCardShowing(false)
        },
        async handleVerifyEmail() {
            this.setEmailSentHavingIssue(false)
            this.isLoading = true;
            const sendUsername = this.isModified ? this.pendingUsername : this.getUsername;
            const sendInfo = await profileUtils.sendVerificationEmail(sendUsername);
            if(sendInfo.status == 200) {
                if(this.isPendingStatus) this.setResendVerifyEmail(true)
                this.setPendingStatus(true)
            } else {
                this.setErrorMsg(this.sharedStrings.email_verification.email_verify_card_error_content)
                this.setEmailSentHavingIssue(true)
            }
            this.isLoading=false
        },
        async handleCloseDialog() {
            this.emailDialog = false;
        }
    }
}
</script>

<style lang="scss">
.ns-card {
  margin-bottom: 10px;
  .medium-p {
    margin-bottom: 25px;
  }
  .small-p {
    margin-bottom: 15px;
  }
  .subhead-l {
    text-transform: lowercase;
    font-size: 16px;
    line-height: 21px;
  }
   .resend-p {
        margin-bottom: 15px;
        text-transform: uppercase;
    }
}
</style>
